import { Injectable, inject } from '@angular/core';
import { NavigationError } from '@angular/router';
import { Store } from '@ngrx/store';
import { Capacitor } from '@capacitor/core';
import {
  SpxToasterAutoCloseSpeedEnum,
  spxToasterActions,
} from '@softpak/components/spx-toaster';

@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  private static readonly chunkReloadStorageKey =
    'orders-chunk-reload-attempted';

  private readonly appStore = inject(Store);
  private chunkReloadInProgress = false;
  private initialized = false;

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  clearReloadAttempt(): void {
    sessionStorage.removeItem(ChunkLoadRecoveryService.chunkReloadStorageKey);
  }

  handleNavigationError(error: NavigationError): void {
    if (this.isChunkLoadError(error.error)) {
      this.handleChunkLoadFailure(error.error);
    }
  }

  private readonly onUnhandledRejection = (
    event: PromiseRejectionEvent
  ): void => {
    if (this.isChunkLoadError(event.reason)) {
      event.preventDefault();
      this.handleChunkLoadFailure(event.reason);
    }
  };

  private handleChunkLoadFailure(error: unknown): void {
    if (this.chunkReloadInProgress) {
      return;
    }

    if (Capacitor.getPlatform() !== 'web') {
      console.error('Chunk load failed outside web platform.', error);
      return;
    }

    this.chunkReloadInProgress = true;

    const alreadyRetried =
      sessionStorage.getItem(
        ChunkLoadRecoveryService.chunkReloadStorageKey
      ) === '1';

    if (!alreadyRetried) {
      sessionStorage.setItem(
        ChunkLoadRecoveryService.chunkReloadStorageKey,
        '1'
      );
      this.appStore.dispatch(
        spxToasterActions.createWarning({
          messageText: 'New version available, page will be reloaded.',
          autoClose: SpxToasterAutoCloseSpeedEnum.SLOW,
        })
      );

      setTimeout(() => {
        window.location.reload();
      }, 1500);
      return;
    }

    sessionStorage.removeItem(ChunkLoadRecoveryService.chunkReloadStorageKey);
    this.appStore.dispatch(
      spxToasterActions.createError({
        messageText:
          'Unable to load the latest app version automatically. Please refresh the page manually.',
        autoClose: SpxToasterAutoCloseSpeedEnum.SLOW,
      })
    );

    console.error('Chunk load failed after automatic reload attempt.', error);
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = this.getReadableErrorReason(error).toLowerCase();

    return (
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('importing a module script failed') ||
      message.includes('loading chunk') ||
      message.includes('chunkloaderror')
    );
  }

  private getReadableErrorReason(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }

    if (error == null) {
      return '';
    }

    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
}
